import React, { useEffect, useState } from 'react';

interface VotingCardsProps {
    onVote: (value: string) => void;
    selectedValue: string | null;
    disabled: boolean;
    cardValues: string[];
}

const VotingCards: React.FC<VotingCardsProps> = ({
    onVote,
    selectedValue,
    disabled,
    cardValues,
}) => {
    const [keyboardVote, setKeyboardVote] = useState<string | null>(null);
    const [recentlySelected, setRecentlySelected] = useState<string | null>(
        null,
    );

    useEffect(() => {
        if (selectedValue === null) {
            setKeyboardVote(null);
            setRecentlySelected(null);
        }
    }, [selectedValue]);

    useEffect(() => {
        if (
            selectedValue &&
            selectedValue !== recentlySelected &&
            !keyboardVote
        ) {
            setRecentlySelected(selectedValue);
            const timer = setTimeout(() => {
                setRecentlySelected(null);
            }, 3700);
            return () => clearTimeout(timer);
        }
    }, [selectedValue, keyboardVote]);

    const getKeyboardMapping = (): { [key: string]: number } => {
        const mapping: { [key: string]: number } = {};

        for (let i = 1; i <= 9; i++) {
            mapping[i.toString()] = i - 1;
        }
        mapping['0'] = 9;

        const letters = [
            'q',
            'w',
            'e',
            'r',
            't',
            'y',
            'u',
            'i',
            'o',
            'p',
            'a',
            's',
            'd',
            'f',
            'g',
            'h',
            'j',
            'k',
            'l',
            'z',
            'x',
            'c',
            'v',
            'b',
            'n',
            'm',
        ];

        letters.forEach((letter, index) => {
            mapping[letter] = index + 10;
        });

        mapping['='] = -1;

        return mapping;
    };

    const keyboardMapping = getKeyboardMapping();

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (disabled) return;

            const keyPressed = event.key.toLowerCase();

            if (keyPressed in keyboardMapping) {
                const index = keyboardMapping[keyPressed];

                if (keyPressed === '=' && cardValues.includes('?')) {
                    if (selectedValue === '?') {
                        setKeyboardVote(null);
                        onVote('');
                    } else {
                        setKeyboardVote('?');
                        onVote('?');
                    }
                    return;
                }

                if (index >= 0 && index < cardValues.length) {
                    const value = cardValues[index];
                    if (selectedValue === value) {
                        setKeyboardVote(null);
                        onVote('');
                    } else {
                        setKeyboardVote(value);
                        onVote(value);
                    }
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onVote, disabled, cardValues, keyboardMapping, selectedValue]);

    const getShortcutKey = (value: string, index: number): string => {
        if (value === '?') return '=';

        for (const [key, idx] of Object.entries(keyboardMapping)) {
            if (idx === index) return key.toUpperCase();
        }

        return '';
    };

    const cardSizeClass =
        cardValues.length > 8 ? 'w-16 h-24 text-xl' : 'w-20 h-28 text-2xl';

    const getTextSizeClass = (value: string): string => {
        const length = value.length;
        if (length > 10) return 'text-xs';
        if (length > 6) return 'text-sm';
        if (length > 4) return 'text-base';
        return cardValues.length > 8 ? 'text-xl' : 'text-2xl';
    };

    return (
        <div className="my-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-gray-300">
                Your Vote
            </h3>

            <div className="flex flex-wrap gap-3 justify-center">
                {cardValues.map((value, index) => {
                    const isSelected =
                        selectedValue === value && keyboardVote !== value;
                    const isRecentlySelected = recentlySelected === value;

                    return (
                        <button
                            key={value}
                            onClick={() => {
                                setKeyboardVote(null);
                                if (selectedValue === value) {
                                    onVote('');
                                } else {
                                    onVote(value);
                                }
                            }}
                            disabled={disabled}
                            className={`
                                ${cardSizeClass}
                                relative 
                                flex flex-col items-center justify-center
                                font-bold rounded-lg
                                transform transition-all duration-200
                                ${
                                    isSelected
                                        ? 'bg-primary-light text-white shadow-lg ring-2 ring-primary-light'
                                        : 'bg-white dark:bg-dark-card text-gray-700 dark:text-gray-200 shadow border border-gray-200 dark:border-dark-border hover:shadow-md hover:-translate-y-1'
                                }
                                ${isRecentlySelected ? 'animate-pulse' : ''}
                                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            `}
                        >
                            <span className="text-sm absolute top-1 right-1 opacity-50 dark:opacity-70">
                                {getShortcutKey(value, index)}
                            </span>
                            <span
                                className={`${getTextSizeClass(value)} text-center px-1 break-all overflow-hidden whitespace-normal leading-tight`}
                            >
                                {value.length > 30
                                    ? value.substring(0, 30) + '...'
                                    : value}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                <p className="flex items-center">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                    </svg>
                    <strong>Keyboard shortcuts:</strong>
                    <span className="ml-1">
                        Use keys 1-9, 0 for the first 10 cards, then
                        Q,W,E,R,T,Y... for additional cards. Press = for '?'
                    </span>
                </p>

                {keyboardVote && (
                    <p className="flex items-center mt-2 text-secondary-dark dark:text-secondary-light">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                        <strong>Vote submitted via keyboard</strong>
                        <span className="ml-1 text-xs opacity-70">
                            (not visible to others when sharing screen)
                        </span>
                    </p>
                )}
            </div>
        </div>
    );
};

export default VotingCards;
